/**
 * §P.7 · Los datos del club.
 *
 * ## Dos campos que no son papeleo
 *
 * **`legal_name` y `tax_id` salen impresos en los recibos.** Un club que emite
 * doscientos al mes con el NIF mal puesto tiene un problema con Hacienda, no con la
 * aplicación. Van juntos y con ese aviso al lado.
 *
 * **`timezone` manda sobre todo lo que tenga hora**: el horario de silencio, la
 * generación de sesiones y el vencimiento de la remesa. Cambiarla mueve las tres, y
 * la pantalla lo dice antes de que alguien la toque «por si acaso».
 *
 * ## El slug no se edita
 *
 * Está en la URL de la ficha pública del club y en los enlaces que ya se han
 * compartido. Cambiarlo rompería los que hay y no hay endpoint que lo permita: se
 * enseña como dato, no como campo.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fecha } from '@/shared/ui/formato';
import type { Club } from '@/club/ajustes/tipos';

interface Campos {
  trade_name: string;
  legal_name: string;
  tax_id: string;
  email: string;
  phone: string;
  website: string;
  timezone: string;
}

const desde = (c: Club): Campos => ({
  trade_name: c.trade_name,
  legal_name: c.legal_name ?? '',
  tax_id: c.tax_id ?? '',
  email: c.email ?? '',
  phone: c.phone ?? '',
  website: c.website ?? '',
  timezone: c.timezone,
});

const oNulo = (v: string): string | null => (v.trim() === '' ? null : v.trim());

export function DatosDelClub({
  club,
  puedeEditar,
}: {
  club: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [campos, setCampos] = useState<Campos | null>(null);
  const [sembradoDe, setSembradoDe] = useState<string | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [guardado, setGuardado] = useState(false);

  const datos = useQuery({
    queryKey: ['club', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Club;
    },
  });

  // Se siembra durante el render y no desde un efecto: un `setState` en un efecto
  // pinta una vez con el formulario vacío. Mismo patrón que la escalera de grados.
  if (datos.data && sembradoDe !== club) {
    setSembradoDe(club);
    setCampos(desde(datos.data));
  }

  const guardar = useMutation({
    mutationFn: async (c: Campos) => {
      const r = await api.v1.clubs({ club }).patch({
        trade_name: c.trade_name.trim(),
        legal_name: oNulo(c.legal_name),
        tax_id: oNulo(c.tax_id),
        email: oNulo(c.email),
        phone: oNulo(c.phone),
        website: oNulo(c.website),
        timezone: c.timezone.trim(),
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as Club;
    },
    onSuccess: () => {
      setGuardado(true);
      void cache.invalidateQueries({ queryKey: ['club', club] });
      // La cabecera enseña el nombre comercial: si cambió, hay que repintarla.
      void cache.invalidateQueries({ queryKey: ['branding', club] });
    },
    onError: (e) => {
      setGuardado(false);
      setProblema(aProblema(e));
    },
  });

  if (datos.isPending || !campos) {
    return <Cargando filas={6} etiqueta="Cargando el club" />;
  }

  if (datos.isError || !datos.data) {
    return (
      <PanelError
        problema={aProblema(datos.error)}
        reintentar={() => void datos.refetch()}
      />
    );
  }

  const c = datos.data;
  const cambiar = (campo: keyof Campos, valor: string) => {
    setCampos((f) => (f ? { ...f, [campo]: valor } : f));
    setGuardado(false);
  };

  const zonaCambiada = campos.timezone.trim() !== c.timezone;

  return (
    <form
      className="formulario"
      onSubmit={(e) => {
        e.preventDefault();
        setProblema(null);
        guardar.mutate(campos);
      }}
    >
      <h1>Datos del club</h1>

      <dl className="datos">
        <div>
          <dt>Dirección pública</dt>
          {/* El slug no se edita: está en enlaces que ya se han compartido. */}
          <dd>
            <code>kiapclubs.com/clubes/{c.slug}</code>
          </dd>
        </div>
        <div>
          <dt>Alumnos activos</dt>
          <dd>{c.counts.active_students}</dd>
        </div>
        <div>
          <dt>Sedes</dt>
          <dd>{c.counts.venues}</dd>
        </div>
        <div>
          <dt>En Kiap desde</dt>
          <dd>{fecha(c.onboarded_at)}</dd>
        </div>
      </dl>

      <h2 className="seccion">Cómo se llama</h2>

      <div className="rejilla">
        <label>
          Nombre comercial
          <input
            required
            maxLength={160}
            value={campos.trade_name}
            onChange={(e) => cambiar('trade_name', e.target.value)}
            disabled={!puedeEditar}
          />
        </label>

        <label>
          Razón social
          <input
            maxLength={200}
            placeholder="Club Deportivo Dojang Pascual"
            value={campos.legal_name}
            onChange={(e) => cambiar('legal_name', e.target.value)}
            disabled={!puedeEditar}
          />
        </label>

        <label>
          NIF o CIF
          <input
            maxLength={20}
            placeholder="G12345678"
            value={campos.tax_id}
            onChange={(e) => cambiar('tax_id', e.target.value)}
            disabled={!puedeEditar}
          />
        </label>
      </div>

      {/* No es papeleo: va impreso en cada recibo. */}
      <div className="nota">
        <strong>La razón social y el NIF salen impresos en los recibos.</strong>{' '}
        Doscientos recibos al mes con el NIF mal puesto es un problema con
        Hacienda, no con la aplicación.
      </div>

      <h2 className="seccion">Cómo contactar</h2>

      <div className="rejilla">
        <label>
          Correo
          <input
            type="email"
            maxLength={200}
            placeholder="info@dojangpascual.es"
            value={campos.email}
            onChange={(e) => cambiar('email', e.target.value)}
            disabled={!puedeEditar}
          />
        </label>

        <label>
          Teléfono
          <input
            maxLength={30}
            placeholder="600 11 22 33"
            value={campos.phone}
            onChange={(e) => cambiar('phone', e.target.value)}
            disabled={!puedeEditar}
          />
        </label>

        <label>
          Web
          <input
            maxLength={200}
            placeholder="https://dojangpascual.es"
            value={campos.website}
            onChange={(e) => cambiar('website', e.target.value)}
            disabled={!puedeEditar}
          />
        </label>
      </div>

      <h2 className="seccion">La hora</h2>

      <div className="rejilla">
        <label>
          Zona horaria
          <input
            required
            maxLength={60}
            placeholder="Europe/Madrid"
            value={campos.timezone}
            onChange={(e) => cambiar('timezone', e.target.value)}
            disabled={!puedeEditar}
          />
        </label>
      </div>

      {/*
        El aviso solo cuando de verdad se está cambiando: puesto siempre sería un
        cartel que nadie lee, y esto hay que leerlo.
      */}
      {zonaCambiada ? (
        <div className="aviso" role="status">
          <strong>Vas a cambiar la zona horaria del club.</strong> Manda sobre
          todo lo que tiene hora: el horario de silencio de los avisos, la
          generación de las clases y el vencimiento de los recibos. Las tres
          cosas se mueven.
        </div>
      ) : (
        <p className="tenue">
          Manda sobre el horario de silencio, la generación de clases y el
          vencimiento de los recibos.
        </p>
      )}

      {problema && <PanelError problema={problema} />}

      {guardado && !guardar.isPending && (
        <div className="aviso aviso-calma" role="status">
          <strong>Guardado.</strong>
        </div>
      )}

      {puedeEditar && (
        <div className="acciones">
          <button type="submit" disabled={guardar.isPending}>
            {guardar.isPending ? 'Guardando…' : 'Guardar los datos'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={guardar.isPending}
            onClick={() => {
              setCampos(desde(c));
              setProblema(null);
              setGuardado(false);
            }}
          >
            Deshacer los cambios
          </button>
        </div>
      )}
    </form>
  );
}
